// Matches the Worker: stale only after NOAA misses a three-hour publication cycle.
export const WEATHER_AGE_LIMIT = 6.5 * 60 * 60 * 1000;
export function weatherEndpoint(value, base) {
  const url = new URL(value, base);
  const parent = new URL(base);
  const local = url.origin === parent.origin && ['127.0.0.1', 'localhost'].includes(url.hostname);
  if ((!local && url.protocol !== 'https:') || url.username || url.password || url.search || url.hash || url.pathname !== '/v1/space-weather') throw new Error('Invalid weather endpoint');
  return url.href;
}
export function weatherView(data, now = Date.now()) {
  if (!data || !['recent', 'stale'].includes(data.status) || !Array.isArray(data.observations) || !data.observations.length || data.observations.length > 8) throw new Error('Observations unavailable');
  const parsedTime = value => {
    if (typeof value !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value)) throw new Error('Invalid timestamp');
    const date = new Date(value);
    if (!Number.isFinite(date.getTime()) || date.toISOString() !== value || date.getTime() > now + 300000) throw new Error('Invalid timestamp');
    return date;
  };
  const rows = data.observations.map(item => {
    const date = parsedTime(item.intervalStart);
    if (typeof item.kp !== 'number' || !Number.isFinite(item.kp) || item.kp < 0 || item.kp > 9) throw new Error('Invalid reading');
    return {time: date.getTime(), iso: date.toISOString(), kp: item.kp, label: date.toISOString().slice(5, 16).replace('T', ' ') + ' UTC'};
  });
  if (rows.some((row, i) => i && row.time <= rows[i - 1].time)) throw new Error('Invalid ordering');
  const latest = rows.at(-1);
  if (data.latest?.intervalStart !== latest.iso || data.latest?.kp !== latest.kp) throw new Error('Inconsistent reading');
  const retrieved = parsedTime(data.retrievedAt);
  return {status: data.status === 'stale' || now - latest.time > WEATHER_AGE_LIMIT ? 'stale' : 'recent', rows, latest, retrieved: retrieved.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'};
}
export function readChecklist(raw, allowedIds) {
  if (!raw || raw.length > 10000) return null;
  try {
    const data = JSON.parse(raw);
    if (data.version !== 1 || !Array.isArray(data.checked) || data.checked.some(id => typeof id !== 'string')) return null;
    return [...new Set(data.checked.filter(id => allowedIds.includes(id)))];
  } catch { return null; }
}
const BrowserElement = globalThis.HTMLElement ?? class {};
class FlareChecklist extends BrowserElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.inputs = [...this.querySelectorAll('[data-step]')];
    this.remember = this.querySelector('[data-remember]');
    this.note = this.querySelector('[data-storage-note]');
    this.key = this.dataset.storageKey;
    this.querySelector('[data-enhanced]').hidden = false;
    try {
      const saved = readChecklist(localStorage.getItem(this.key), this.inputs.map(input => input.dataset.step));
      if (saved) {
        this.remember.checked = true;
        this.inputs.forEach(input => { input.checked = saved.includes(input.dataset.step); });
      }
    } catch { this.storageFailure(); }
    this.addEventListener('change', () => { this.update(); this.save(); }, {signal: this.abort.signal});
    this.querySelector('[data-reset]').addEventListener('click', () => {
      this.inputs.forEach(input => { input.checked = false; }); this.update(); this.save();
    }, {signal: this.abort.signal});
    this.querySelector('[data-print]').addEventListener('click', () => window.print(), {signal: this.abort.signal});
    this.update();
  }
  storageFailure() { this.remember.checked = false; this.note.textContent = 'Saving is unavailable in this browser. Your checklist still works for this visit.'; }
  update() {
    const done = this.inputs.filter(input => input.checked).length;
    this.querySelector('[data-progress]').textContent = done + ' of ' + this.inputs.length + ' starting steps checked' + (done === this.inputs.length ? '. Keep reviewing your plan as your needs change.' : '.');
    this.querySelector('progress').value = done;
  }
  save() {
    try {
      if (this.remember.checked) localStorage.setItem(this.key, JSON.stringify({version: 1, checked: this.inputs.filter(input => input.checked).map(input => input.dataset.step)}));
      else localStorage.removeItem(this.key);
    } catch { this.storageFailure(); }
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; }
}
class FlareWeather extends BrowserElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    this.button = this.querySelector('[data-refresh]');
    if (!this.dataset.endpoint) return;
    try {
      const endpoint = weatherEndpoint(this.dataset.endpoint, window.location.href);
      if (this.endpoint !== endpoint) this.lastData = null;
      this.endpoint = endpoint;
    }
    catch { this.showUnavailable(); return; }
    this.button.hidden = false;
    this.button.addEventListener('click', () => this.refresh(), {signal: this.events.signal});
    const resume = () => {
      if (document.hidden) return;
      this.renderRetained();
      this.refresh();
    };
    document.addEventListener('visibilitychange', resume, {signal: this.events.signal});
    window.addEventListener('pageshow', resume, {signal: this.events.signal});
    this.renderRetained();
    this.refresh();
    this.timer = setInterval(resume, 300000);
  }
  showUnavailable() {
    this.lastData = null;
    this.querySelector('[data-weather-status]').textContent = 'Observations unavailable';
    this.querySelector('[data-kp]').textContent = '—';
    this.querySelector('[data-period]').textContent = 'Open NOAA’s report for official updates.';
    this.querySelector('[data-retrieved]').textContent = '';
    this.querySelector('[data-history]').replaceChildren();
    this.querySelector('[data-history-note]').textContent = 'No current data could be verified. This does not mean conditions are quiet.';
  }
  renderRetained() {
    if (!this.lastData) return;
    try { this.renderView(weatherView(this.lastData)); }
    catch { this.showUnavailable(); }
  }
  renderView(view) {
    this.querySelector('[data-weather-status]').textContent = view.status === 'stale' ? 'Older observations · update delayed' : 'Recent NOAA observations';
    this.querySelector('[data-kp]').textContent = view.latest.kp.toFixed(2).replace(/\.00$/, '');
    this.querySelector('[data-period]').textContent = 'Three-hour interval starting ' + view.latest.label;
    this.querySelector('[data-retrieved]').textContent = 'Data retrieved ' + view.retrieved;
    this.querySelector('[data-history-note]').textContent = view.status === 'stale' ? 'These readings are older than our freshness limit. Check NOAA for official updates.' : 'A record of reported activity, not a forecast.';
    const bars = view.rows.map(row => {
      const li = document.createElement('li');
      const bar = document.createElement('span'); bar.className = 'flare-kp-bar'; bar.style.height = (Math.max(row.kp, 0.12) / 9 * 100) + '%'; bar.setAttribute('aria-hidden', 'true');
      const value = document.createElement('strong'); value.textContent = row.kp.toFixed(2).replace(/\.00$/, '');
      const time = document.createElement('time'); time.dateTime = row.iso; time.textContent = row.label;
      li.append(bar, value, time); return li;
    });
    this.querySelector('[data-history]').replaceChildren(...bars);
  }
  async refresh() {
    if (this.loading || !this.events || !this.isConnected) return;
    this.loading = true; this.button.disabled = true; this.setAttribute('aria-busy', 'true');
    this.renderRetained();
    if (!this.lastData) this.querySelector('[data-weather-status]').textContent = 'Checking NOAA observations…';
    const lifecycle = this.events;
    const requestAbort = new AbortController();
    this.requestAbort = requestAbort;
    const current = () => this.isConnected && this.events === lifecycle && this.requestAbort === requestAbort && !lifecycle.signal.aborted;
    const timeout = setTimeout(() => requestAbort.abort(), 7000);
    this.requestTimer = timeout;
    try {
      const response = await fetch(this.endpoint, {signal: requestAbort.signal, credentials: 'omit', referrerPolicy: 'no-referrer', redirect: 'error'});
      if (!response.ok) throw new Error('Service unavailable');
      const data = await response.json();
      const view = weatherView(data);
      if (!current()) return;
      this.lastData = data;
      this.renderView(view);
    } catch { if (current()) this.showUnavailable(); }
    finally {
      clearTimeout(timeout);
      if (current()) { this.loading = false; this.button.disabled = false; this.removeAttribute('aria-busy'); this.requestAbort = null; this.requestTimer = null; }
    }
  }
  disconnectedCallback() {
    this.events?.abort(); this.events = null;
    this.requestAbort?.abort(); this.requestAbort = null;
    clearTimeout(this.requestTimer); clearInterval(this.timer);
    this.loading = false;
    if (this.button) this.button.disabled = false;
    this.removeAttribute('aria-busy');
  }
}
if (globalThis.customElements) {
  if (!customElements.get('flare-checklist')) customElements.define('flare-checklist', FlareChecklist);
  if (!customElements.get('flare-weather')) customElements.define('flare-weather', FlareWeather);
}
